import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Nav from "@/components/Nav";

export default function Login() {
  const { user, login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/dashboard";

  if (user) return <Navigate to={nextUrl} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(nextUrl);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-6 hidden lg:block">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">Return to your desk</p>
          <h1 className="font-editorial text-7xl leading-[0.95] mt-4">Welcome<br/>back.</h1>
          <p className="mt-6 text-[color:var(--text-muted)] max-w-md">
            Pick up where you left off — your drafts, chapters and cover art are exactly where you left them.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-6">
          <div className="max-w-md ml-auto bg-white border border-[color:var(--border)] p-10">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-2">§ Log in</p>
            <h2 className="font-editorial text-3xl mb-8">Sign in to BookForge</h2>
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <label className="block">
                <span className="block text-xs uppercase tracking-widest mb-1">Email</span>
                <input
                  type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                  className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid="login-email"
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-widest mb-1">Password</span>
                <input
                  type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                  className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                  data-testid="login-password"
                />
              </label>
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </button>
              <div className="flex items-center justify-between text-sm text-[color:var(--text-muted)]">
                <Link to="/forgot-password" className="link-underline text-[color:var(--text)]"
                       data-testid="login-forgot-link">Forgot password?</Link>
                <span>
                  No account? <Link to="/register" className="link-underline text-[color:var(--text)]" data-testid="login-register-link">Create one</Link>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
